// packages/shared/src/client/safe-redirect.ts
// Source unique de validation des cibles de redirection cross-app.
//
// Contexte sécurité : le handoff d'authentification Hub -> sous-app transporte
// l'access token dans un fragment (#token / #nf_token), jamais en query.
// de l'URL de retour. Une cible non validée transforme donc un simple open
// redirect en exfiltration de token, donc en prise de compte.
//
// Cette validation ne remplace pas celle de l'API (auth.service.ts
// validateRedirectUrl) : elle la double côté client, là où le token est
// réellement recollé à l'URL.

type Env = Record<string, string | undefined>

/** Env vars listant les origines des apps de l'écosystème (build Vite). */
const APP_URL_ENV_KEYS = [
  'VITE_HUB_URL',
  'VITE_ACADEMY_URL',
  'VITE_PROMPTS_URL',
  'VITE_FORGE_URL',
  'VITE_MADSAAS_URL',
] as const

function readEnv(env?: Env): Env {
  if (env) return env
  return (import.meta.env ?? {}) as unknown as Env
}

/**
 * Origines autorisées comme cible de retour : l'app courante + les apps
 * NeuroForge déclarées en env. Toute valeur non parsable est ignorée plutôt
 * que d'élargir l'allowlist par accident.
 */
export function allowedAppOrigins(currentOrigin: string, env?: Env): Set<string> {
  const origins = new Set<string>()

  const add = (value?: string): void => {
    if (!value) return
    try {
      origins.add(new URL(value).origin)
    } catch {
      // Env var mal renseignée -> on n'autorise rien de plus.
    }
  }

  add(currentOrigin)
  const resolved = readEnv(env)
  for (const key of APP_URL_ENV_KEYS) add(resolved[key])

  return origins
}

/**
 * Résout une cible de redirection non fiable en URL sûre.
 *
 * Rejette (retour sur la racine de l'app courante) :
 * - les origines hors allowlist (`https://evil.tld`)
 * - les échappements par antislash (`/\evil.tld`, `//evil.tld`) — le parseur
 *   WHATWG les normalise en `//`, l'origine change donc et le contrôle joue
 * - les pseudo-schémas (`javascript:`, `data:`) via le filtre de protocole
 * - les URLs malformées
 */
export function safeRedirectTarget(
  raw: string | null | undefined,
  currentOrigin: string = window.location.origin,
  env?: Env,
): URL {
  const fallback = new URL('/', currentOrigin)

  const value = raw?.trim()
  if (!value) return fallback

  try {
    const target = new URL(value, currentOrigin)
    if (target.protocol !== 'http:' && target.protocol !== 'https:') return fallback
    if (target.username || target.password) return fallback
    // Do not let an allowed-origin deep link launder credentials through query,
    // nested redirect parameters or another hash. The actual auth handoff is
    // appended exclusively by the Hub to the top-level URL fragment.
    const forbidden =
      /^(?:token|nf_token|access_token|accessToken|refresh_token|refreshToken|authorization)$/i
    if ([...target.searchParams.keys()].some((key) => forbidden.test(key))) return fallback
    if (
      [...target.searchParams.values()].some((value) =>
        /(?:^|[?#&])(?:token|nf_token|access_token|accessToken|refresh_token|refreshToken)=/i.test(
          value,
        ),
      )
    )
      return fallback
    const fragmentParams = new URLSearchParams(target.hash.replace(/^#/, ''))
    if ([...fragmentParams.keys()].some((key) => forbidden.test(key))) return fallback
    if (
      [...fragmentParams.values()].some((value) =>
        /(?:^|[?#&])(?:token|nf_token|access_token|accessToken|refresh_token|refreshToken)=/i.test(
          value,
        ),
      )
    )
      return fallback
    return allowedAppOrigins(currentOrigin, env).has(target.origin) ? target : fallback
  } catch {
    return fallback
  }
}

/** Chemin interne (path + query + hash) d'une cible same-origin, pour `navigate()`. */
export function internalPathOf(target: URL): string {
  return `${target.pathname}${target.search}${target.hash}` || '/'
}
